/**
 * Answer comparison for Grammatik.
 *
 * This app deliberately does NOT use `isCorrectAnswer` from `@/core/normalize`,
 * and that is the most important fact in this file.
 *
 * That helper accepts anything within Levenshtein distance 1, which is right
 * for a vocabulary app — a one-character slip on "cachorro" should not cost the
 * user a streak. Applied to grammar it destroys the deck: `den` and `dem` are
 * one edit apart, as are `der`/`den`, `ihn`/`ihm`, `ein`/`eine` and
 * `hat`/`hast`. Those pairs are not typos of each other, they are the entire
 * content of lessons 12, 13, 14, 28 and 29. A typo-tolerant comparison marks
 * the single most important A1 distinction correct no matter which side of it
 * the user picks, and the deck silently stops measuring anything.
 *
 * So: exact match after a conservative normalization, and no edit distance.
 * What the normalization does forgive is spelled out below, and each item is
 * forgiven because it is not the thing being taught.
 */
import {
  isGrammarAnswerCorrect as sharedIsGrammarAnswerCorrect,
  isOrderCorrect as sharedIsOrderCorrect,
} from '../shared/grammar-answer';

/**
 * Umlaut transliteration, the standard German one.
 *
 * Applied to BOTH sides, so `faehrst` matches `fährst`. Deliberately NOT a fold
 * to the bare vowel: `fahrst` must stay wrong, because lesson 16 is about
 * exactly that umlaut. This is the line between "your keyboard lacks a key" and
 * "you do not know the form", and it is the whole reason this is a
 * transliteration table rather than a diacritic strip.
 */
const TRANSLITERATIONS: [RegExp, string][] = [
  [/ä/g, 'ae'],
  [/ö/g, 'oe'],
  [/ü/g, 'ue'],
  [/ß/g, 'ss'],
];

/**
 * Normalize one side of a grammar comparison.
 *
 * Forgives, in order: surrounding whitespace, repeated internal whitespace,
 * a single trailing sentence period, and (unless the exercise opts out) case.
 *
 * Does not forgive: word order, missing words, wrong endings, wrong umlauts,
 * or any punctuation other than that one trailing period. A comma before a
 * subordinate clause is not decoration in German, and an app that shrugs at it
 * teaches that it is optional.
 */
export function normalizeGrammarAnswer(text: string, caseSensitive = false): string {
  let out = text.trim().replace(/\s+/g, ' ').replace(/\.$/, '').trim();
  if (!caseSensitive) out = out.toLowerCase();
  for (const [pattern, replacement] of TRANSLITERATIONS) {
    out = out.replace(pattern, replacement);
  }
  return out;
}

/**
 * Membership and order comparison come from the shared module — see its header
 * for why the split falls where it does. Bound to this normalizer so callers in
 * the German app keep importing from one place.
 */
export function isGrammarAnswerCorrect(
  input: string,
  accepted: string[],
  caseSensitive = false,
): boolean {
  return sharedIsGrammarAnswerCorrect(normalizeGrammarAnswer, input, accepted, caseSensitive);
}

export function isOrderCorrect(input: string[], accepted: string[][]): boolean {
  return sharedIsOrderCorrect(normalizeGrammarAnswer, input, accepted);
}
